import { useId } from "react";
import { useT } from "../settings/context";
import { Button } from "./Button";
import { useDialog } from "./dialog";

/**
 * A QUESTION BEFORE SOMETHING IRREVERSIBLE.
 *
 * A native `<dialog>`, opened with `showModal()` — the mechanics live in
 * `useDialog`, shared with `Modal`. Not a hand-rolled overlay: the element brings
 * the things such overlays get wrong — focus moved in and trapped, Escape closing
 * it, the rest of the page made inert, and a backdrop that is a real pseudo-element
 * rather than a div to stack.
 *
 * ⚠️ NOT `window.confirm`. It blocks the whole tab, it cannot be styled, and on a
 * phone it renders as a system alert bearing the browser's own hostname — which on
 * an event site reads as something having gone wrong.
 *
 * Displayed strings go through `t()`, French kept at the call site as the
 * fallback: what the site says of itself follows the language being read.
 */
export function Confirm({
  open,
  title,
  message,
  confirmLabel,
  cancelLabel,
  onConfirm,
  onCancel,
  busy = false,
  emphasis = "confirm",
}: {
  open: boolean;
  title: string;
  message: string;
  confirmLabel: string;
  cancelLabel?: string;
  onConfirm: () => void;
  onCancel: () => void;
  /**
   * L'ACTION EST EN COURS : le bouton qui confirme tourne, et n'est plus cliquable.
   *
   * ⚠️ SANS ÇA, LA QUESTION ELLE-MÊME DEVIENT LA FAILLE. La boîte ne se referme
   * qu'une fois le serveur revenu : entre les deux, le bouton reste vivant, et deux
   * clics envoient deux fois la même suppression. La seconde revient en erreur et
   * affiche un refus pour un geste qui a pourtant réussi. Le geste précédent portait
   * ce garde-fou sur ses boutons en ligne ; il ne devait pas se perdre en passant
   * dans une vraie fenêtre.
   *
   * Le bouton qui annule, lui, reste atteignable : une fenêtre dont on ne peut plus
   * sortir tant que le réseau n'a pas répondu est un piège.
   */
  busy?: boolean;
  /**
   * WHICH BUTTON IS EMPHASISED, and it is not always the confirming one.
   *
   * A dialog that exists to catch an ACCIDENT must emphasise the safe way out: an
   * idle second click lands on the prominent button, and if that button is the one
   * that loses the work, the guard becomes the trap. Emphasise the confirming
   * action only where confirming is the point (deleting something on purpose).
   */
  emphasis?: "confirm" | "cancel";
}) {
  const t = useT();
  const dialog = useDialog(open, onCancel);
  /* Named for a screen reader, which otherwise announces "dialog" and nothing else. */
  const titleId = useId();
  /* Le défaut passe par le dictionnaire, comme tout ce que ce site dit de lui-même :
     écrit dans la signature, il aurait servi « Annuler » à un site anglais. */
  const cancel = cancelLabel ?? t("CANCEL", "Annuler");

  return (
    <dialog {...dialog} className="ask" aria-labelledby={titleId}>
      <div className="ask__b">
        <b id={titleId}>{title}</b>
        <p>{message}</p>
        {/* The emphasised one LAST — same reading order as `.actions`: what you are
            expected to press sits where the eye finishes. */}
        <div className="ask__a">
          {emphasis === "cancel" ? (
            <>
              <Button
                type="button"
                variant="ghost"
                loading={busy}
                onClick={onConfirm}
              >
                {confirmLabel}
              </Button>
              <Button type="button" onClick={onCancel}>
                {cancel}
              </Button>
            </>
          ) : (
            <>
              <Button type="button" variant="ghost" onClick={onCancel}>
                {cancel}
              </Button>
              <Button type="button" loading={busy} onClick={onConfirm}>
                {confirmLabel}
              </Button>
            </>
          )}
        </div>
      </div>
    </dialog>
  );
}
